// components/DashboardGrid.js - Updated to use real data for short trend windows
import React, { useState, useEffect } from 'react';
import { dashboardStyles } from '../dashboardStyles';
import { useDashboardData } from '../hooks/useDashboardData'; // ✅ FIXED: Correct import path
import MetricModules from './MetricModules';
import OriginsModule from './OriginsModule';
import DestinationsModule from './DestinationsModule';
import SidsSnapshotModule from './SidsSnapshotModule';
const TREND_WINDOWS = [
    { label: '12H', hours: 12 },
    { label: '24H', hours: 24 }
];

const TREND_MODES = [
    { label: 'By Range Count', value: 'per_range' },
    { label: 'By SID Count', value: 'per_sid' }
];

const DashboardGrid = ({ stats, tableSize, uniqueDestinations, topDestinations, selectedTable, refreshToken }) => {
    const [timeWindowHours, setTimeWindowHours] = useState(12);
    const [trendMode, setTrendMode] = useState('per_range');

    // ✅ NEW: Use real dashboard data from a configurable time window
    const {
        origins: realOrigins,
        destinations: realDestinations,
        uniqueDestinations: realUniqueDestinations,
        loading: dashboardLoading,
        error: dashboardError,
        lastUpdated,
        cached,
        cacheAge,
        dataPeriod,
        originPeriod,
        autoRefresh,
        setAutoRefresh,
        refreshData,
        isStale
    } = useDashboardData(selectedTable, timeWindowHours, trendMode, 3);

    const handleTimeWindowChange = (hours) => {
        setTimeWindowHours(hours);
    };

    useEffect(() => {
        if (refreshToken > 0) {
            refreshData();
        }
    }, [refreshToken, refreshData]);

    // Debug logging
    useEffect(() => {
        if (realOrigins.length > 0 || realDestinations.length > 0) {
            console.log('📊 Real dashboard data loaded:', {
                origins: realOrigins.length,
                destinations: realDestinations.length,
                uniqueDestinations: realUniqueDestinations,
                period: dataPeriod?.description,
                cached: cached,
                cacheAge: cacheAge
            });
        }
    }, [realOrigins, realDestinations, realUniqueDestinations, dataPeriod, cached, cacheAge]);

    // Use real data if available, fallback to passed props
    const displayOrigins = realOrigins.length > 0 ? realOrigins : [];
    const displayDestinations = realDestinations.length > 0 ? realDestinations : topDestinations;
    const displayUniqueDestinations = realUniqueDestinations > 0 ? realUniqueDestinations : uniqueDestinations;

    return (
        <div style={dashboardStyles.dashboardGrid}>
            {/* Metric Modules */}
            <MetricModules stats={stats} tableSize={tableSize} />

            <div style={dashboardStyles.timeWindowModule} data-trend-window-version="12h-24h-sid-range">
                <div style={dashboardStyles.timeWindowHeader}>
                    <div>
                        <div style={dashboardStyles.timeWindowTitle}>Trend Window</div>
                        <div style={dashboardStyles.timeWindowSubtitle}>
                            Best Destinations, SIDs & Ranges
                        </div>
                    </div>
                    <div style={dashboardStyles.timeWindowHint}>Live v2</div>
                </div>

                <div style={dashboardStyles.timeWindowButtons}>
                    {TREND_WINDOWS.map((windowOption) => (
                        <button
                            key={`trend-window-${windowOption.hours}`}
                            type="button"
                            onClick={() => handleTimeWindowChange(windowOption.hours)}
                            style={{
                                ...dashboardStyles.timeWindowButton,
                                ...(timeWindowHours === windowOption.hours ? dashboardStyles.timeWindowButtonActive : {})
                            }}
                        >
                            {windowOption.label}
                        </button>
                    ))}
                </div>

                <div style={{
                    ...dashboardStyles.timeWindowButtons,
                    marginTop: '10px'
                }}>
                    {TREND_MODES.map((modeOption) => (
                        <button
                            key={`trend-mode-${modeOption.value}`}
                            type="button"
                            onClick={() => setTrendMode(modeOption.value)}
                            style={{
                                ...dashboardStyles.timeWindowButton,
                                ...(trendMode === modeOption.value ? dashboardStyles.timeWindowButtonActive : {})
                            }}
                        >
                            {modeOption.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* ✅ UPDATED: Origins Module with real data */}
            <OriginsModule
                topOrigins={displayOrigins}
                loading={dashboardLoading}
                error={dashboardError}
                lastUpdated={lastUpdated}
                cached={cached}
                cacheAge={cacheAge}
                dataPeriod={originPeriod || dataPeriod}
                autoRefresh={autoRefresh}
                onToggleAutoRefresh={() => setAutoRefresh(!autoRefresh)}
                onRefresh={refreshData}
                isStale={isStale}
            />

            <SidsSnapshotModule />

            {/* ✅ UPDATED: Destinations Module with real data */}
            <DestinationsModule
                uniqueDestinations={displayUniqueDestinations}
                topDestinations={displayDestinations}
                loading={dashboardLoading}
                error={dashboardError}
                lastUpdated={lastUpdated}
                cached={cached}
                cacheAge={cacheAge}
                dataPeriod={dataPeriod}
                trendMode={trendMode}
                autoRefresh={autoRefresh}
                onToggleAutoRefresh={() => setAutoRefresh(!autoRefresh)}
                onRefresh={refreshData}
                isStale={isStale}
            />
        </div>
    );
};

export default DashboardGrid;
